import React, { useEffect, useState } from 'react';
import {
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Select,
  Input,
  Modal,
  message,
  InputNumber,
} from 'antd';
import dayjs from 'dayjs';
import request from '@/utils/request';
import BundleDetailDrawer from './BundleDetailDrawer';

interface Row {
  id: number;
  title: string;
  status: 'draft' | 'pending_review' | 'published' | 'rejected' | 'offline';
  priceCoins: number | null;
  priceRmbCents: number | null;
  heat: number;
  salesCount: number;
  publishedAt: string | null;
  creatorId: number;
  creatorEmail: string | null;
  creatorDisplayName: string | null;
}

const STATUS_LABEL: Record<Row['status'], { text: string; color: string }> = {
  draft: { text: '草稿', color: 'default' },
  pending_review: { text: '待审核', color: 'processing' },
  published: { text: '已发布', color: 'success' },
  rejected: { text: '已驳回', color: 'error' },
  offline: { text: '已下架', color: 'warning' },
};

const BundleReviewQueuePage: React.FC = () => {
  const [items, setItems] = useState<Row[]>([]);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<string>('pending_review');
  const [keyword, setKeyword] = useState('');

  const [rejectTarget, setRejectTarget] = useState<Row | null>(null);
  const [reason, setReason] = useState('');
  const [heatTarget, setHeatTarget] = useState<Row | null>(null);
  const [heatValue, setHeatValue] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const res = await request.get<{ items: Row[]; total: number }>('/admin/bundles', {
        params: { page, pageSize, status: status || undefined, keyword: keyword || undefined },
      });
      const data = res.data as unknown as { items: Row[]; total: number };
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status, keyword]);

  const approve = async (id: number) => {
    try {
      await request.post(`/admin/bundles/${id}/review`, { action: 'approve' });
      message.success('已通过');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const reject = async () => {
    if (!rejectTarget) return;
    if (!reason.trim()) {
      message.error('请填写驳回理由');
      return;
    }
    try {
      await request.post(`/admin/bundles/${rejectTarget.id}/review`, {
        action: 'reject',
        reason,
      });
      message.success('已驳回');
      setRejectTarget(null);
      setReason('');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const patchHeat = async () => {
    if (!heatTarget) return;
    try {
      await request.patch(`/admin/bundles/${heatTarget.id}`, { heatBoost: heatValue });
      message.success('已更新热度');
      setHeatTarget(null);
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const takeOffline = async (id: number) => {
    try {
      await request.patch(`/admin/bundles/${id}`, { status: 'offline' });
      message.success('已下架');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          套图审核 / 管理
        </Typography.Title>
        <Space>
          <Select
            style={{ width: 160 }}
            value={status}
            onChange={(v) => setStatus(v)}
            options={[
              { label: '全部', value: '' },
              { label: '待审核', value: 'pending_review' },
              { label: '已发布', value: 'published' },
              { label: '已驳回', value: 'rejected' },
              { label: '已下架', value: 'offline' },
              { label: '草稿', value: 'draft' },
            ]}
          />
          <Input.Search
            placeholder="搜索标题"
            allowClear
            style={{ width: 240 }}
            onSearch={(v) => {
              setPage(1);
              setKeyword(v);
            }}
          />
        </Space>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 70 },
          { title: '标题', dataIndex: 'title' },
          {
            title: '创作者',
            render: (_, row) => row.creatorDisplayName || row.creatorEmail || `#${row.creatorId}`,
          },
          {
            title: '状态',
            dataIndex: 'status',
            render: (v) => {
              const l = STATUS_LABEL[v as Row['status']];
              return <Tag color={l?.color ?? 'default'}>{l?.text ?? v}</Tag>;
            },
          },
          {
            title: '价格',
            render: (_, r) =>
              r.priceRmbCents != null ? `¥${(r.priceRmbCents / 100).toFixed(2)}` : '—',
          },
          {
            title: '销量/热度',
            render: (_, r) => `${r.salesCount} / ${r.heat}`,
          },
          {
            title: '发布时间',
            dataIndex: 'publishedAt',
            render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
          },
          {
            title: '操作',
            render: (_, row) => (
              <Space>
                <Button
                  size="small"
                  type="link"
                  onClick={() => setDetailId(row.id)}
                >
                  详情
                </Button>
                {row.status === 'pending_review' && (
                  <>
                    <Button size="small" type="link" onClick={() => approve(row.id)}>
                      通过
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={() => setRejectTarget(row)}
                    >
                      驳回
                    </Button>
                  </>
                )}
                {row.status === 'published' && (
                  <Button size="small" type="link" danger onClick={() => takeOffline(row.id)}>
                    下架
                  </Button>
                )}
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    setHeatTarget(row);
                    setHeatValue(0);
                  }}
                >
                  调热度
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={!!rejectTarget}
        title={`驳回 ${rejectTarget?.title}`}
        onOk={reject}
        onCancel={() => {
          setRejectTarget(null);
          setReason('');
        }}
      >
        <Input.TextArea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="请填写清晰的驳回理由，创作者会收到消息"
        />
      </Modal>

      <Modal
        open={!!heatTarget}
        title={`调整 ${heatTarget?.title} 虚拟热度`}
        onOk={patchHeat}
        onCancel={() => setHeatTarget(null)}
      >
        <Typography.Paragraph type="secondary">
          虚拟热度加到公式 heat = 基础值(按套图id 稳定 60–240) + salesCount×15 + heatBoost 上，影响首页排序。
        </Typography.Paragraph>
        <InputNumber
          value={heatValue}
          onChange={(v) => setHeatValue(Number(v ?? 0))}
          min={-10000}
          max={100000}
          style={{ width: '100%' }}
        />
      </Modal>

      <BundleDetailDrawer
        bundleId={detailId}
        open={detailId !== null}
        onClose={() => setDetailId(null)}
        onChanged={load}
      />
    </Space>
  );
};

export default BundleReviewQueuePage;
